import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { batchAdminApi, type SmsLogEntry } from '../../api/serverCheck.js';
import { Spinner, ErrorBox, Field } from '../../components/ui.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { phone as formatPhone } from '../../lib/format.js';

/**
 * 서버체크 > 문자 발송 이력.
 *
 * 배치가 실제로 보낸 문자를 본다. 한 줄이 **발송 한 번**이다 - 수신자가
 * 여럿이어도 본문은 하나이므로(migrations/015), 목록에서 발송을 고르면 오른쪽에
 * 본문 전체와 누가 받았는지가 나온다.
 *
 * ── 왜 목록·상세 두 칸인가 ──────────────────────────────────────────────────
 * 처음에는 배치관리 화면 안의 표 하나였다. 그런데 LMS 본문이 수백 자라 표
 * 안에서는 접어 둘 수밖에 없었고, 펴면 줄이 화면을 덮어 다른 발송을 훑을 수
 * 없었다. 목록은 짧게 유지하고 본문은 옆에서 통째로 본다.
 *
 * 본문에 매출·회원수가 그대로 들어 있어 **최고 관리자만** 볼 수 있다(서버도
 * requireRole('admin') 으로 다시 막는다).
 */

/** 오늘부터 n일 전 (YYYY-MM-DD). */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const today = () => daysAgo(0);

export function SmsLogPage() {
  const { user } = useAuth();
  const canRead = (user?.authtype ?? '').split(',').map((s) => s.trim()).includes('admin');

  const [filters, setFilters] = useState({
    job: '',
    failedOnly: false,
    from: daysAgo(7),
    to: today(),
  });
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const jobs = useQuery({ queryKey: ['batch-admin'], queryFn: batchAdminApi.list, enabled: canRead });
  const log = useQuery({
    queryKey: ['batch-admin', 'sms-log', filters, page],
    queryFn: () =>
      batchAdminApi.smsLog({
        ...(filters.job === '' ? {} : { job: filters.job }),
        ...(filters.failedOnly ? { failedOnly: true } : {}),
        from: filters.from,
        to: filters.to,
        page,
      }),
    enabled: canRead,
  });

  if (!canRead) {
    return (
      <div className="page">
        <div className="notice notice--warn">최고 관리자만 볼 수 있는 화면입니다.</div>
      </div>
    );
  }

  const entries = log.data?.entries ?? [];
  const selected = entries.find((e) => e.id === selectedId) ?? entries[0] ?? null;
  const sendingJobs = (jobs.data?.jobs ?? []).filter((j) => j.sends);
  const titleOf = (name: string) =>
    jobs.data?.jobs.find((j) => j.name === name)?.title ?? name;

  const change = (patch: Partial<typeof filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
    setSelectedId(null);
  };

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1 className="page-title">
            문자 발송 이력
            <span className="page-title__sub">배치가 실제로 보낸 SMS · LMS</span>
          </h1>
        </div>
        {log.isFetching && <span className="page-head__busy">불러오는 중…</span>}
      </header>

      {log.data && !log.data.storeReady ? (
        <div className="notice notice--warn">
          발송 이력 표가 아직 적용되지 않았습니다. 지금 보내는 문자는 기록되지 않습니다 —{' '}
          <code>migrations/015_xadmin_batch_sms_log_per_send.sql</code> 을 적용하세요.
        </div>
      ) : (
        <>
          {log.data && (
            <div className="summary">
              <div className="summary__item">
                <span className="summary__label">오늘</span>
                <span className="summary__value tabular">{log.data.recent.today}회</span>
              </div>
              <div className="summary__item">
                <span className="summary__label">최근 7일</span>
                <span className="summary__value tabular">{log.data.recent.sends}회</span>
              </div>
              <div className="summary__item">
                <span className="summary__label">그중 실패 섞인 발송</span>
                <span
                  className={`summary__value tabular${log.data.recent.failedSends > 0 ? ' smslog-bad' : ''}`}
                >
                  {log.data.recent.failedSends}회
                </span>
              </div>
            </div>
          )}

          <div className="filters">
            <Field label="시작일">
              <input
                className="input"
                type="date"
                value={filters.from}
                max={filters.to}
                onChange={(e) => change({ from: e.target.value })}
              />
            </Field>
            <Field label="종료일">
              <input
                className="input"
                type="date"
                value={filters.to}
                min={filters.from}
                onChange={(e) => change({ to: e.target.value })}
              />
            </Field>
            <Field label="배치">
              <select
                className="input"
                value={filters.job}
                onChange={(e) => change({ job: e.target.value })}
              >
                <option value="">전체</option>
                {sendingJobs.map((j) => (
                  <option key={j.name} value={j.name}>
                    {j.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="결과">
              <select
                className="input"
                value={filters.failedOnly ? 'failed' : 'all'}
                onChange={(e) => change({ failedOnly: e.target.value === 'failed' })}
              >
                <option value="all">전체</option>
                <option value="failed">실패 섞인 것만</option>
              </select>
            </Field>
            <button
              type="button"
              className="btn btn--ghost filters__reset"
              onClick={() => change({ job: '', failedOnly: false, from: daysAgo(7), to: today() })}
            >
              초기화
            </button>
          </div>

          <ErrorBox error={log.error ?? jobs.error} />

          {log.isLoading ? (
            <Spinner />
          ) : entries.length === 0 ? (
            <div className="card">
              <p className="td-muted">
                {filters.failedOnly
                  ? '이 기간에 실패한 발송이 없습니다.'
                  : '이 기간에 보낸 문자가 없습니다.'}
              </p>
              <p className="td-muted smslog-empty-hint">
                배치를 아직 켜지 않았다면 <b>서버체크 &gt; 배치관리</b>에서 켤 수 있습니다. 레거시
                PHP 크론이 보낸 문자는 여기 남지 않습니다.
              </p>
            </div>
          ) : (
            <div className="smslog">
              <ol className="smslog-list">
                {entries.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      className={`smslog-item${selected?.id === e.id ? ' is-selected' : ''}`}
                      aria-current={selected?.id === e.id}
                      onClick={() => setSelectedId(e.id)}
                    >
                      <span className="smslog-item__top">
                        <ResultBadge entry={e} />
                        <span className="td-muted smslog-item__time">
                          {e.sentAt.slice(5, 16)}
                        </span>
                      </span>
                      <span className="smslog-item__job">
                        {titleOf(e.jobName)}
                        <span className="badge badge--info">{e.kind.toUpperCase()}</span>
                      </span>
                      <span className="smslog-item__peek">{firstLine(e.message)}</span>
                    </button>
                  </li>
                ))}
              </ol>

              {selected && <SendDetail entry={selected} title={titleOf(selected.jobName)} />}
            </div>
          )}

          <div className="batch-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={page <= 1}
              onClick={() => {
                setPage((p) => p - 1);
                setSelectedId(null);
              }}
            >
              이전
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!log.data?.hasMore}
              onClick={() => {
                setPage((p) => p + 1);
                setSelectedId(null);
              }}
            >
              다음
            </button>
            <span className="td-muted">{page}쪽</span>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 발송 한 번의 결과.
 *
 * 한 발송에 여러 명이 들어가므로 성공/실패 둘로는 부족하다 - 섞인 경우를
 * 따로 보여줘야 "3명 중 1명만 못 받았다"를 알 수 있다.
 */
function ResultBadge({ entry }: { entry: SmsLogEntry }) {
  if (entry.failCount === 0) {
    return <span className="badge badge--ok">성공 {entry.okCount}</span>;
  }
  if (entry.okCount === 0) {
    return <span className="badge badge--danger">실패 {entry.failCount}</span>;
  }
  return (
    <span className="badge badge--warn">
      일부 실패 {entry.failCount}/{entry.totalCount}
    </span>
  );
}

function SendDetail({ entry, title }: { entry: SmsLogEntry; title: string }) {
  return (
    <section className="card smslog-detail">
      <div className="card__head">
        <h2 className="card__title">
          {title}
          <span className="card__count">{entry.sentAt}</span>
        </h2>
        <ResultBadge entry={entry} />
      </div>

      <dl className="deflist">
        <dt>보낸 번호</dt>
        <dd className="tabular">{formatPhone(entry.sender)}</dd>
        <dt>종류</dt>
        <dd>
          {entry.kind.toUpperCase()} · {entry.message.length}자
        </dd>
      </dl>

      <h3 className="smslog-h3">본문</h3>
      {/* 줄바꿈이 실제 문자와 같이 보여야 한다. */}
      <pre className="batch-preview__body">{entry.message || '(빈 문안)'}</pre>

      <h3 className="smslog-h3">받는 사람 {entry.totalCount}명</h3>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 140 }}>번호</th>
            <th style={{ width: 70 }}>결과</th>
            <th>실패 사유</th>
          </tr>
        </thead>
        <tbody>
          {entry.recipients.map((r) => (
            <tr key={r.phone}>
              <td className="tabular">{formatPhone(r.phone)}</td>
              <td>
                <span className={`badge ${r.ok ? 'badge--ok' : 'badge--danger'}`}>
                  {r.ok ? '성공' : '실패'}
                </span>
              </td>
              <td className={r.ok ? 'td-muted' : 'smslog-bad'}>{r.ok ? '—' : (r.detail ?? '알 수 없음')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** 목록에서 본문 대신 보여줄 한 줄. */
function firstLine(message: string): string {
  const line = message.split('\n').find((l) => l.trim() !== '') ?? '(빈 문안)';
  return line.length > 34 ? `${line.slice(0, 34)}…` : line;
}
